import React, { useState, useEffect } from 'react';
import '../styles/Factures.css';
import { FaFileInvoiceDollar, FaPrint, FaEye } from 'react-icons/fa';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { API } from '../config/api';


export default function Factures() {
  const [factures, setFactures] = useState([]);
  const [searchClient, setSearchClient] = useState('');
  // Set current month as default (YYYY-MM format)
  const [searchPeriode, setSearchPeriode] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 7);
  });
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [viewingClient, setViewingClient] = useState(null);
  
  // Get unique periods for filter dropdown
  const [uniquePeriods, setUniquePeriods] = useState([]);
  const [uniqueClients, setUniqueClients] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [uniqueSecteurs, setUniqueSecteurs] = useState([]);

  const token = localStorage.getItem('token');

  // Helper function to normalize status values
  const normalizeStatus = (status) => {
    if (!status) return status;
    
    // Normalize different variations of unpaid status
    if (status === 'non payé' || status === 'non payée' || status === 'impayé' || status === 'impayée') {
      return 'impayée';
    }
    // Normalize different variations of paid status
    if (status === 'payé' || status === 'payée' || status === 'paye' || status === 'payee') {
      return 'payée';
    }
    return status;
  };

  // Helper function to check if status is paid
  const isPaidStatus = (status) => {
    return normalizeStatus(status) === 'payée';
  };

  useEffect(() => {
    fetchFactures();
    fetchSecteurs();
  }, []);

  const fetchFactures = async () => {
    try {
      const res = await axios.get(API+'/api/factures', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFactures(res.data);
      
      // Extract unique periods and clients for filters
      const periods = [...new Set(res.data.map(f => f.periode))].sort((a, b) => b.localeCompare(a));
      const clients = [...new Set(res.data.map(f => `${f.nomclient} ${f.prenomclient}`))].sort();
      
      // Normalize statuses and get unique values
      const rawStatuses = res.data.map(f => f.status).filter(s => s);
      const normalizedStatuses = rawStatuses.map(status => normalizeStatus(status));
      const statuses = [...new Set(normalizedStatuses)].sort();
      
      setUniquePeriods(periods);
      setUniqueClients(clients);
      setUniqueStatuses(statuses);
      
      console.log('📊 Raw statuses found:', rawStatuses);
      console.log('📊 Normalized unique statuses:', statuses);
    } catch (err) {
      console.error('Erreur chargement factures:', err);
    }
  };

  const fetchSecteurs = async () => {
    try {
      const res = await axios.get(API + '/api/secteurs', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const secteurs = Array.isArray(res.data) ? res.data : [];
      setUniqueSecteurs(secteurs.map(s => ({ id: s.ids, nom: s.nom })));
    } catch (err) {
      console.error('Erreur récupération secteurs:', err);
      setUniqueSecteurs([]);
    }
  };

  const filteredFactures = factures.filter(f => {
    const fullName = `${f.nomclient} ${f.prenomclient}`;
    
    // Multi-search: Enhanced search - support partial search by client name, reference, or contract
    const searchLower = searchClient.toLowerCase();
    const clientMatch = searchClient === '' || 
      fullName.toLowerCase().includes(searchLower) ||
      (f.reference && f.reference.toLowerCase().includes(searchLower)) ||
      (f.codeqr && f.codeqr.toLowerCase().includes(searchLower));
    
    // Period filter: check if search is empty or if it matches
    const periodMatch = searchPeriode === '' || f.periode === searchPeriode;
    
    // Status filter: normalize both filter value and facture status for comparison
    const statusMatch = filterStatus === '' || normalizeStatus(f.status) === filterStatus;
    
    // Secteur filter: check secteur_id
    const secteurId = f.secteur_id || f.ids || f.idsecteur;
    const secteurMatch = filterSecteur === '' || (secteurId ? secteurId.toString() === filterSecteur : false);
    
    return clientMatch && periodMatch && statusMatch && secteurMatch;
  });

  const groupByClient = () => {
    const map = {};
    for (let f of factures) {
      const key = `${f.nomclient}-${f.prenomclient}`;
      if (!map[key]) map[key] = [];
      map[key].push(f);
    }
    return map;
  };

const handleDownloadPDF = async (facture) => {
  const elementId = `facture-pdf-${facture.reference}`;
  const element = document.getElementById(elementId);

  if (!element) {
    alert('❌ Impossible de générer le PDF : élément introuvable.');
    return;
  }

  console.log("DEBUG facture ➤", facture);

  // 🔍 Vérification des données
  const isValid = facture.detailsTranches?.every(t =>
    typeof t.volume === 'number' && !isNaN(t.volume) &&
    typeof t.prix === 'number' && !isNaN(t.prix) &&
    typeof t.montant === 'number' && !isNaN(t.montant)
  );

  if (!isValid) {
    console.error("❌ Données invalides détectées :", facture.detailsTranches);
    alert("Erreur : certaines données de la facture sont invalides (volume, prix ou montant).");
    return;
  }

  try {
    // 💡 Masquer les styles pouvant poser problème
    element.style.boxShadow = 'none';
    element.style.background = 'white';

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

    if (imgHeight <= pageHeight) {
      pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight);
    } else {
      const totalPages = Math.ceil(imgHeight / pageHeight);
      for (let i = 0; i < totalPages; i++) {
        const sourceY = i * pageHeight;
        const canvasSlice = document.createElement('canvas');
        canvasSlice.width = canvas.width;
        canvasSlice.height = (pageHeight * canvas.width) / pageWidth;

        const ctx = canvasSlice.getContext('2d');
        ctx.drawImage(
          canvas,
          0, sourceY * (canvas.height / imgHeight),
          canvas.width, canvasSlice.height,
          0, 0,
          canvas.width, canvasSlice.height
        );

        const imgSlice = canvasSlice.toDataURL('image/png');
        if (i > 0) pdf.addPage();
        pdf.addImage(imgSlice, 'PNG', 0, 0, pageWidth, pageHeight);
      }
    }

    pdf.save(`facture-${facture.reference}.pdf`);
  } catch (err) {
    console.error('❌ Erreur génération PDF :', err);
    alert('Erreur lors de la génération du PDF.');
  }
};




  const handlePrintAll = (clientKey) => {
  const list = groupByClient()[clientKey];
  if (!list || list.length === 0) return;

  const [nom, prenom] = clientKey.split('-');
  const win = window.open('', '_blank');

  let facturesHTML = '';

  list.forEach((f, idx) => {
    const details = f.detailsTranches || [];

    const tranchesHTML = details.map(t => `
      <tr>
        <td>${t.tranche}</td>
        <td>${t.volume}</td>
        <td>${t.prix.toFixed(2)} DH/m³</td>
        <td>${t.montant.toFixed(2)} DH</td>
      </tr>
    `).join('');

    facturesHTML += `
      <div class="facture-page">
        <div class="entete">
          <h2>💧 Société d'Eau</h2>
          <p><strong>Facture N°:</strong> ${f.reference}</p>
          <p><strong>Date:</strong> ${new Date(f.date).toLocaleDateString()}</p>
          <p><strong>Client:</strong> ${f.nomclient} ${f.prenomclient}</p>
          <p><strong>Contrat:</strong> ${f.codeqr}</p>
          <p><strong>Période:</strong> ${f.periode}</p>
        </div>

        <table class="table">
          <thead>
            <tr>
              <th>Tranche</th>
              <th>Volume</th>
              <th>Prix unitaire</th>
              <th>Montant</th>
            </tr>
          </thead>
          <tbody>
            ${tranchesHTML}
            <tr>
              <td colspan="3" style="text-align:right;"><strong>Total TTC :</strong></td>
              <td><strong>${f.montant.toFixed(2)} DH</strong></td>
            </tr>
          </tbody>
        </table>

        <p class="footer">Page ${idx + 1} / ${list.length}</p>
      </div>
    `;
  });

  win.document.write(`
    <html>
    <head>
      <title>Factures - ${nom} ${prenom}</title>
      <style>
        @media print {
          .facture-page {
            page-break-after: always;
          }
        }

        body {
          font-family: 'Arial', sans-serif;
          padding: 30px;
          margin: 0;
          background: #fff;
        }

        .facture-page {
          padding: 20px;
          border: 1px solid #ccc;
          margin-bottom: 40px;
          background-color: #fdfdfd;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
        }

        .entete h2 {
          margin-bottom: 10px;
          color: #007BFF;
        }

        .entete p {
          margin: 2px 0;
        }

        .table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 15px;
        }

        .table th, .table td {
          border: 1px solid #444;
          padding: 8px;
          text-align: center;
        }

        .footer {
          text-align: right;
          margin-top: 15px;
          font-style: italic;
          color: #666;
        }
      </style>
    </head>
    <body>
      <h1 style="text-align:center;">🧾 Factures du client : ${nom} ${prenom}</h1>
      ${facturesHTML}
    </body>
    </html>
  `);

  win.document.close();
  win.print();
};


  const handlePrintOne = (facture) => {
    const win = window.open('', '_blank');
    const tranches = facture.detailsTranches || [];

    const tranchesHTML = tranches.map((t, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${t.tranche}</td>
        <td>${t.volume.toFixed(2)}</td>
        <td>${t.prix.toFixed(2)} DH</td>
        <td>${t.montant.toFixed(2)} DH</td>
      </tr>
    `).join('');

    const totalMontant = facture.montant.toFixed(2);
    const forfait = 20.00;
    const montantFinal = (parseFloat(totalMontant) + forfait).toFixed(2);

    win.document.write(`
      <html dir="rtl">
      <head>
        <title>فاتورة ${facture.reference}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; direction: rtl; position: relative; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px; }
          th, td { border: 1px solid #333; padding: 6px; text-align: center; }
          .header-table td { border: none; }
          .title { text-align: center; font-weight: bold; font-size: 18px; margin-bottom: 10px; }
          .section { margin-top: 20px; }
          .footer { margin-top: 30px; font-size: 12px; text-align: center; }
          .ltr { direction: ltr; text-align: left; }
          .association-name { position: absolute; top: 10px; left: 10px; font-weight: bold; font-size: 16px; color: #007BFF; }
          @media print {
            .association-name { position: absolute; top: 0; left: 0; }
          }
        </style>
      </head>
      <body>

        <div class="association-name">Association Abar</div>

        <table class="header-table">
          <tr>
            <td><strong>جماعة راس الواد</strong><br/>إقليم تاونات</td>
            <td class="title" colspan="3">💧 <br/>FACTURATION CONSOMMATION EAU<br/>فاتورة استهلاك الماء</td>
            <td><strong>Commune Ras L'Oued</strong><br/>Province de Taounate</td>
          </tr>
        </table>
        
        <h6>${facture.reference}</h6>

        <div class="section">
          <table>
            <tr>
              <th>الإسم و النسب<br/>Nom/Prénom</th>
              <td colspan="2">${facture.nomclient} ${facture.prenomclient}</td>
              <th>العنوان<br/>Adresse</th>
              <td colspan="2">${facture.adresseclient || facture.adresse || '---'}</td>
            </tr>
            <tr>
              <th>رقم العداد<br/>N° Compteur</th>
              <td>${facture.marquecompteur || '---'}</td>
              <th>الشهر المفوتر<br/>Mois Facturé</th>
              <td>${facture.periode || '---'}</td>
              <th>السنة<br/>Année</th>
              <td>${new Date(facture.date).getFullYear()}</td>
            </tr>
            <tr>
              <th>البيان القديم<br/>Ancien Index</th>
              <td>${facture.consommationpre}</td>
              <th>البيان الجديد<br/>Nouveau</th>
              <td>${facture.consommationactuelle}</td>
              <th>الإستهلاك بالمتر³<br/>Consommation</th>
              <td>${facture.volume.toFixed(2)}</td>
            </tr>
          </table>
        </div>

        <div class="section">
          <table>
            <thead>
              <tr>
                <th>رقم الشطر<br/>#</th>
                <th>الشق<br/>Tranches</th>
                <th>الكمية<br/>Relevé (m³)</th>
                <th>الثمن dh/m³<br/>Tarif</th>
                <th>المبلغ<br/>Valeur à payer</th>
              </tr>
            </thead>
            <tbody>
              ${tranchesHTML}
              <tr>
                <td colspan="2"><strong>مجموع المتر³<br/>Total m³</strong></td>
                <td>${facture.volume.toFixed(2)}</td>
                <td colspan="2"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <table>
            <tr>
              <td colspan="4"><strong>Forfait mensuel 10 dh/mois</strong></td>
              <td>${forfait.toFixed(2)} DH</td>
            </tr>
            <tr>
              <td colspan="4"><strong>الصافي الواجب أداؤه<br/>Total à payer</strong></td>
              <td><strong>${montantFinal} DH</strong></td>
            </tr>
          </table>
        </div>

        <div class="footer">
          <p>هام جدا : تؤدى 20 درهما غرامة عن كل تأخير في الأداء. الأجل المحدد للأداء هو أسبوع واحد بعد تسليم الفاتورة</p>
        </div>

        <div style="text-align: center; margin-top: 20px; padding: 10px;">
          <img src="https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(facture.codeqr)}&size=100x100" alt="QR Code" style="border: 1px solid #333; padding: 3px;" />
          <p style="font-size: 10px; margin-top: 3px;">${facture.codeqr}</p>
        </div>
      </body>
      </html>
    `);

    win.document.close();
    win.print();
  };
  const handleResendEmail = async (idfact) => {
  if (!window.confirm("Renvoyer l’e-mail de facture ?")) return;

  try {
    const res = await axios.post(API+`/api/factures/${idfact}/renvoyer-email`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    alert(res.data.message);
    fetchFactures(); // recharger les factures pour mettre à jour emailenvoye
  } catch (err) {

    console.error("Erreur renvoi e-mail :", err);
    alert("Échec du renvoi d'e-mail ❌");
  }
};
const toggleStatus = async (facture) => {
  // Allow toggling between both statuses using normalized values
  const currentNormalizedStatus = normalizeStatus(facture.status);
  const newStatus = currentNormalizedStatus === 'payée' ? 'impayée' : 'payée';
  
  const confirmMessage = newStatus === 'payée' 
    ? `💰 Confirmer le paiement de cette facture ?\n\n📋 Détails:\n• Client: ${facture.nomclient} ${facture.prenomclient}\n• Référence: ${facture.reference}\n• Montant: ${facture.montant.toFixed(2)} DH\n• Période: ${facture.periode}\n\n✅ Cette action marquera la facture comme PAYÉE.`
    : `⚠️ Annuler le paiement de cette facture ?\n\n📋 Détails:\n• Client: ${facture.nomclient} ${facture.prenomclient}\n• Référence: ${facture.reference}\n• Montant: ${facture.montant.toFixed(2)} DH\n• Période: ${facture.periode}\n\n❌ Cette action marquera la facture comme IMPAYÉE.`;
  
  if (!window.confirm(confirmMessage)) {
    return;
  }

  try {
    await axios.patch(
      API+`/api/factures/${facture.idfact}/statut`,
      { status: newStatus },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Update local state immediately for better UX
    setFactures(prev => prev.map(f => 
      f.idfact === facture.idfact 
        ? { ...f, status: newStatus }
        : f
    ));
    
    alert(`✅ Statut mis à jour: ${newStatus}`);
  } catch (err) {
    console.error("Erreur mise à jour statut :", err);
    alert("❌ Impossible de mettre à jour le statut.");
  }
};


  return (
    <div className="facture-page">
      <h1>Liste des Factures</h1>

      <div className="facture-filters-row">
        <input
          type="text"
          placeholder="Rechercher (nom, réf, contrat)..."
          value={searchClient}
          onChange={e => setSearchClient(e.target.value)}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0', 
            minWidth: '200px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
          title="Rechercher par nom client, référence facture ou code contrat"
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        />

        <select
          value={filterSecteur}
          onChange={e => setFilterSecteur(e.target.value)}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0',
            minWidth: '140px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            cursor: 'pointer'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <option value="">Tous les secteurs</option>
          {uniqueSecteurs.map(secteur => (
            <option key={secteur.id} value={secteur.id}>{secteur.nom}</option>
          ))}
        </select>

        <input
          type="month"
          value={searchPeriode}
          onChange={e => setSearchPeriode(e.target.value)}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0',
            minWidth: '140px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
          title="Sélectionner une période"
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        />

        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0',
            minWidth: '140px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            cursor: 'pointer'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <option value="">Tous les statuts</option>
          {uniqueStatuses.map(status => (
            <option key={status} value={status}>
              {status === 'payée' || status === 'payee' ? '✅' : '⏳'} {status}
            </option>
          ))}
        </select>
      </div>

     <div className="table-responsive">
       <table className="factures-table">
  <thead>
    <tr>
      <th>Référence</th>
      <th style={{ width: '90px', minWidth: '90px' }}>Client</th>
      <th>Contrat</th>
      <th>Secteur</th>
      <th>Période</th>
      <th>Volume (m³)</th>
      <th>Montant (DH)</th>
      <th>Statut</th>
      <th>Date</th>
      <th>Imprimer</th>
      {/* <th colSpan={2}>Actions</th> */}
    </tr>
  </thead>

  <tbody>
    {filteredFactures.map((f, i) => (
      <React.Fragment key={i}>
        <tr>
          <td>{f.reference}</td>
          <td style={{ 
            width: '90px', 
            minWidth: '90px', 
            fontSize: '0.75em',
            padding: '2px 4px',
            wordBreak: 'break-word'
          }}>
            <div title={`${f.nomclient} ${f.prenomclient}`}>
              {f.nomclient} {f.prenomclient}
            </div>
          </td>
          <td>{f.codeqr}</td>
          <td>
            {(() => {
              const secteurId = f.secteur_id || f.ids || f.idsecteur;
              const secteur = uniqueSecteurs.find(s => s.id === secteurId);
              return secteur ? secteur.nom : "-";
            })()}
          </td>
          <td>{f.periode}</td>
          <td>{f.volume}</td>
          <td>{f.montant.toFixed(2)} DH</td>
          <td>
            <span
              className={`status-badge ${isPaidStatus(f.status) ? 'status-paid' : 'status-unpaid'}`}
              onClick={() => toggleStatus(f)}
              title={isPaidStatus(f.status) ? 'Cliquer pour marquer comme impayée' : 'Cliquer pour marquer comme payée'}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                border: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: isPaidStatus(f.status) ? '#d4edda' : '#f8d7da',
                color: isPaidStatus(f.status) ? '#155724' : '#721c24',
                border: isPaidStatus(f.status) ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
                opacity: 1
              }}
            >
              {isPaidStatus(f.status) ? '✅ Payée' : '⏳ Impayée'}
            </span>
          </td>
          <td>{new Date(f.date).toLocaleDateString()}</td>
          <td>
            <button onClick={() => handlePrintOne(f)} title="Imprimer cette facture">
              <FaPrint />
            </button>
          </td>
          {/* <td>
            <button onClick={() => handleDownloadPDF(f)} title="Télécharger PDF">
              <FaFileInvoiceDollar />
            </button>
          </td> */}
        </tr>
      </React.Fragment>
    ))}
  </tbody>
</table>
</div>

      {/* Hidden PDF templates for each invoice */}
      {filteredFactures.map((f, i) => (
        <div key={`pdf-${i}`} id={`facture-pdf-${f.reference}`} style={{ display: 'none', padding: '20px', background: '#fff', color: '#000' }}>
          <h2>Facture {f.reference}</h2>
          <p><strong>Client :</strong> {f.nomclient} {f.prenomclient}</p>
          <p><strong>Adresse :</strong> {f.adresse || '---'}</p>
          <p><strong>Période :</strong> {f.periode}</p>
          <p><strong>Date :</strong> {new Date(f.date).toLocaleDateString()}</p>

          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #000' }}>Tranche</th>
                <th style={{ border: '1px solid #000' }}>Volume</th>
                <th style={{ border: '1px solid #000' }}>Prix</th>
                <th style={{ border: '1px solid #000' }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              {f.detailsTranches?.map((t, j) => (
                <tr key={j}>
                  <td style={{ border: '1px solid #000' }}>{t.tranche}</td>
                  <td style={{ border: '1px solid #000' }}>{t.volume}</td>
                  <td style={{ border: '1px solid #000' }}>{Number(t.prix || 0).toFixed(2)} DH</td>
                  <td style={{ border: '1px solid #000' }}>{Number(t.montant || 0).toFixed(2)} DH</td>
                </tr>
              ))}
              <tr>
                <td colSpan="3" style={{ textAlign: 'right', border: '1px solid #000' }}><strong>Total</strong></td>
                <td style={{ border: '1px solid #000' }}><strong>{f.montant.toFixed(2)} DH</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      {viewingClient && (
        <div className="facture-modal-overlay">
          <div className="facture-modal-content">
            <h2>Détails des factures - {viewingClient.replace('-', ' ')}</h2>
            <table>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Période</th>
                  <th>Volume</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {groupByClient()[viewingClient]?.map((f, i) => (
                  <React.Fragment key={i}>
                    <tr>
                      <td>{f.reference}</td>
                      <td>{f.periode}</td>
                      <td>{f.volume}</td>
                      <td>{f.montant.toFixed(2)} DH</td>
                    </tr>
                    <tr>
                      <td colSpan="4">
                        <table className="inner-tranche-table">
                          <thead>
                            <tr>
                              <th>Tranche</th>
                              <th>Volume</th>
                              <th>Prix</th>
                              <th>Montant</th>
                            </tr>
                          </thead>
                          <tbody>
                            {f.detailsTranches?.map((t, j) => (
                              <tr key={j}>
                                <td>{t.tranche}</td>
                                <td>{t.volume}</td>
                                <td>{t.prix} DH</td>
                                <td>{t.montant.toFixed(2)} DH</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <div className="facture-modal-actions">
              <button onClick={() => handlePrintAll(viewingClient)}><FaPrint /> Imprimer toutes</button>
              <button onClick={() => setViewingClient(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      <footer className="page-footer">
        <p>© 2025 Abar. Powered by <strong>Jysser Connect</strong></p>
      </footer>
    </div>
  );
}

